import {flowRight as compose} from 'lodash';
import PropTypes from 'prop-types';
import React, {useState, useEffect} from 'react';
import {withTranslation} from 'react-i18next';
import {CancelButton, LoadingContainer, TitleButton} from '../../../common/components/StyledComp';
import {Alert} from 'react-bootstrap';
import {PAYMENT_INTENT_STATUS} from '../../../common/constants';
import {injectStripe} from 'react-stripe-elements';
import {PulseLoader} from 'react-spinners';

const StripePaymentConfirmation = ({t, mutation, extractInvoice, stripe, onCancel, onComplete, onStart, onError, disabled, confirmBtnText}) => {

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [finishOnStripeUrl, setFinishOnStripeUrl] = useState(null);
  const [clickOnStripeUrl, setClickOnStripeUrl] = useState(false);

  useEffect(() => {
    if (stripe) {
      setStripeLoading(false);
    }
  }, [stripe]);

  const onSubmit = (event, mutation) => {
    event.preventDefault();
    if (disabled) {
      return;
    }
    onStart();
    setError(false);
    setErrorMessage(null);
    setStripeLoading(true);

    const finishSuccessfully = () => {
      setStripeLoading(false);
      onComplete();
    };

    const finishWithError = (error) => {
      console.error(error);
      setError(true);
      if (error.message) {
        setErrorMessage(error.message);
      }
      setStripeLoading(false);
      onError(error);
    } ;

    const finishWithPaymentIssue = (url, error) => {
      console.error(error);
      setStripeLoading(false);
      setFinishOnStripeUrl(url);
    };

    return mutation().then(({data}) => {
      let paymentIntent, hostedInvoiceUrl;
      try {
        const invoice = extractInvoice(data);
        paymentIntent = invoice.paymentIntent;
        hostedInvoiceUrl = invoice.hostedInvoiceUrl;
      } catch (err) {
        return finishWithError(err);
      }
      if (!paymentIntent) {
        console.warn('No payment intent provided in the mutation result');
        return finishSuccessfully();
      }
      const {clientSecret, status} = paymentIntent;
      if (status === PAYMENT_INTENT_STATUS.SUCCEEDED || status === PAYMENT_INTENT_STATUS.PROCESSING) {
        return finishSuccessfully();
      } else if (status === PAYMENT_INTENT_STATUS.REQUIRES_CONFIRMATION || status === PAYMENT_INTENT_STATUS.REQUIRES_ACTION) {
        return stripe.confirmCardPayment(clientSecret).then((result) => {
          if (result.error) {
            return finishWithPaymentIssue(hostedInvoiceUrl, result.error);
          } else {
            return finishSuccessfully();
          }
        }).catch(finishWithError);
      } else {
        return finishWithError('Unknown payment intent status');
      }
    }).catch(finishWithError);
  };

  return (
    <>
      {error && (
        <Alert variant={'danger'}>{errorMessage ? errorMessage : t('unknownError')}</Alert>
      )}
      {finishOnStripeUrl !== null ? (
        <>
          <p>
            {t('finalizeOnStripe')}
            <br/>
            <a href={finishOnStripeUrl} target="_blank" rel="noopener noreferrer" onClick={() => setClickOnStripeUrl(true)}>{finishOnStripeUrl}</a>
          </p>
          <div className="popin-buttons">
            <TitleButton disabled={!clickOnStripeUrl} onClick={onComplete}>{t('validate')}</TitleButton>
          </div>
        </>
      ) : (
        <div className="popin-buttons">
          {stripeLoading ? (
            <LoadingContainer>
              <PulseLoader/>
            </LoadingContainer>
          ) : (
            <>
              <TitleButton disabled={disabled} onClick={e => onSubmit(e, mutation)}>{confirmBtnText ? confirmBtnText : t('validate')}</TitleButton>
              <CancelButton type="button" onClick={onCancel} style={{marginTop: 0, width: 'auto'}}>{t('cancel')}</CancelButton>
            </>
          )}
        </div>
      )}
    </>
  );
};

StripePaymentConfirmation.defaultProps = {
  onComplete: () => {},
  onCancel: () => {},
  onStart: () => {},
  onError: () => {},
  disabled: false,
  confirmBtnText: null,
};

export const parentPropTypes = {
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
  onStart: PropTypes.func,
  onError: PropTypes.func,
  disabled: PropTypes.bool,
};

StripePaymentConfirmation.propTypes = {
  t: PropTypes.func.isRequired,
  stripe: PropTypes.object,
  mutation: PropTypes.func.isRequired,
  extractInvoice: PropTypes.func.isRequired,
  confirmBtnText: PropTypes.string,
  ...parentPropTypes,
};

export default compose(
  injectStripe,
  withTranslation('stripe'),
)(StripePaymentConfirmation);
